
// Feature Documentation: docs/features/analysis-results-score-columns.md
import React, { useState, useEffect, useContext } from 'react';
import { callApi } from '../callApi';
import { AppContext } from '../components/AppContext';
import {
  Box, Grid, Flex, Text, Button, Link,
  Table, Thead, Tbody, Tr, Th, Td, VStack, Heading,
  Badge, Spinner, useToast, Modal,
  ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody,
  ModalCloseButton, useDisclosure, Accordion, AccordionItem,
  AccordionButton, AccordionPanel, AccordionIcon, useColorModeValue,
  Select, Input, HStack, IconButton
} from "@chakra-ui/react";
import { ViewIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';

function AnalysisResults() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [filters, setFilters] = useState({
    days_back: '90',
    min_score: '',
    max_score: '',
    limit: '50',
  });

  const { accessToken } = useContext(AppContext);

  // Fixed color values matching AnalyticsDashboard
  const bgColor = 'white';
  const borderColor = 'gray.200';
  const tableHeaderBg = 'gray.50';
  const successBg = useColorModeValue('green.50', 'green.900');
  const warningBg = useColorModeValue('yellow.50', 'yellow.900');
  const errorBg = useColorModeValue('red.50', 'red.900');

  // Centralized helper function for score-based attributes
  const getScoreAttributes = (score) => {
    if (score >= 85) {
      return {
        level: 'High',
        colorScheme: 'green',
        bg: successBg,
      };
    } else if (score >= 50) {
      return {
        level: 'Medium',
        colorScheme: 'yellow',
        bg: warningBg,
      };
    } else {
      return {
        level: 'Low',
        colorScheme: 'red',
        bg: errorBg,
      };
    }
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Handle clearing filters
  const handleClearFilters = () => {
    const defaultFilters = {
      days_back: '90',
      min_score: '',
      max_score: '',
      limit: '50',
    };
    setFilters(defaultFilters);
    loadAnalysisData(defaultFilters); // Pass cleared filters to load immediately
  };

  // Load analysis data
  const loadAnalysisData = async (currentFilters = filters) => {
    setIsLoading(true);
    setError(null);
    
    const params = new URLSearchParams();
    if (currentFilters.days_back) params.append('days_back', currentFilters.days_back);
    if (currentFilters.min_score) params.append('min_score', currentFilters.min_score);
    if (currentFilters.max_score) params.append('max_score', currentFilters.max_score);
    if (currentFilters.limit) params.append('limit', currentFilters.limit);
    const queryString = params.toString();

    try {
      const response = await callApi(`/detailed_medical_necessity_analysis_results?${queryString}`, accessToken);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load analysis data');
      }
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setData(result);
        
        toast({
          title: "Data loaded",
          description: "Analysis data refreshed successfully",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
      } else {
        throw new Error('Failed to load analysis data');
      }
    } catch (err) {
      const errorMessage = `Error loading analysis data: ${err.message}`;
      setError(errorMessage);
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    loadAnalysisData();
  }, []);

  // Get approval assessment score badge color
  const getApprovalScoreBadge = (score) => {
    const attributes = getScoreAttributes(score);
    return { colorScheme: attributes.colorScheme };
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Handle viewing detailed analysis
  const handleViewAnalysis = (analysis) => {
    setSelectedAnalysis(analysis);
    onOpen();
  };

  const handleViewPdf = async (fileId, caseId) => {
    try {
      const response = await callApi('/download_case_file', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: caseId,
          file_id: fileId,
          disposition: 'inline'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to open file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const newTab = window.open(url, '_blank');

      if (newTab) {
        newTab.focus();
      } else {
        alert('Please allow pop-ups for this website to view files.');
      }
    } catch (err) {
      toast({
        title: "Error",
        description: `Could not load PDF: ${err.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Modal for detailed analysis view
  const renderAnalysisModal = () => {
    if (!selectedAnalysis) return null;

    return (
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>
            Medical Necessity Analysis - Case {selectedAnalysis.external_case_id}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <VStack spacing={6} align="stretch">
              {/* Case Info */}
              <Box p={4} borderRadius="md" borderWidth="1px" borderColor="blue.200" bg="blue.50">
                <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
                  <Box>
                    <Text fontSize="sm" color="gray.600">Case ID</Text>
                    <Text fontWeight="bold">{selectedAnalysis.external_case_id}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600">As Is Score</Text>
                    <Badge {...getApprovalScoreBadge(selectedAnalysis.approval_assessment_score)}>
                      {selectedAnalysis.approval_assessment_score}
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600">Best Case Score</Text>
                    {selectedAnalysis.best_case_assessment_score > 0 ? (
                      <Badge {...getApprovalScoreBadge(selectedAnalysis.best_case_assessment_score)}>
                        {selectedAnalysis.best_case_assessment_score}
                      </Badge>
                    ) : (
                      <Text fontSize="sm">-</Text>
                    )}
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600">Claim Type</Text>
                    <Badge colorScheme="gray">{selectedAnalysis.claim_type.toUpperCase()}</Badge>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600">Payer</Text>
                    <Text fontWeight="medium">
                      {selectedAnalysis.payer_type}
                    </Text>
                  </Box>
                </Grid>
              </Box>

              {/* CPT and ICD-10 Codes */}
              <Grid templateColumns="1fr 1fr" gap={6}>
                <Box>
                  <Heading size="sm" mb={3}>CPT Codes</Heading>
                  <VStack spacing={2} align="stretch">
                    {selectedAnalysis.cpt_codes.map((cpt, idx) => (
                      <Box key={idx} p={3} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                        <Text fontWeight="bold">{cpt.code}</Text>
                        <Text fontSize="sm">Units: {cpt.units}</Text>
                      </Box>
                    ))}
                  </VStack>
                </Box>
                <Box>
                  <Heading size="sm" mb={3}>ICD-10 Codes</Heading>
                  <VStack spacing={2} align="stretch">
                    {selectedAnalysis.current_icd10.map((icd, idx) => (
                      <Box key={idx} p={3} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                        <Text fontWeight="bold">{icd.code}</Text>
                        <Text fontSize="sm" color="gray.600">{icd.description}</Text>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              </Grid>

              {/* Analysis Details */}
              <Accordion allowToggle>
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <Heading size="sm">Medical Necessity Analysis</Heading>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <Text whiteSpace="pre-wrap">{selectedAnalysis.analysis.medical_necessity_analysis}</Text>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <Heading size="sm">Approval Assessment</Heading>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <Text whiteSpace="pre-wrap">{selectedAnalysis.analysis.approval_assessment}</Text>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <Heading size="sm">Recommendations</Heading>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <Text whiteSpace="pre-wrap">{selectedAnalysis.analysis.recommendations}</Text>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <Heading size="sm">Supporting Evidence</Heading>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <Text whiteSpace="pre-wrap">{selectedAnalysis.analysis.supporting_evidence}</Text>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

  return (
    <Box>
      <HeaderMenus />
      
      <Box bg={bgColor} p={6} minH="calc(100vh - 60px)">
        {/* Filter Controls */}
        <Box mb={6} p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
          <Heading size="sm" mb={4}>Filters</Heading>
          <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4} alignItems="end">
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">Date Range</Text>
              <Select name="days_back" value={filters.days_back} onChange={handleFilterChange}>
                <option value="1">Last 1 Day</option>
                <option value="2">Last 2 Days</option>
                <option value="3">Last 3 Days</option>
                <option value="4">Last 4 Days</option>
                <option value="5">Last 5 Days</option>
                <option value="6">Last 6 Days</option>
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
                <option value="90">Last 90 Days</option>
                <option value="3650">All Time</option>
              </Select>
            </Box>
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">Results per Page</Text>
              <Select name="limit" value={filters.limit} onChange={handleFilterChange}>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="500">500</option>
              </Select>
            </Box>
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">Min Score</Text>
              <Input 
                type="number" 
                name="min_score" 
                value={filters.min_score} 
                onChange={handleFilterChange} 
                placeholder="e.g., 0"
              />
            </Box>
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">Max Score</Text>
              <Input 
                type="number" 
                name="max_score" 
                value={filters.max_score} 
                onChange={handleFilterChange} 
                placeholder="e.g., 100"
              />
            </Box>
            <HStack spacing={4}>
              <Button colorScheme="blue" onClick={() => loadAnalysisData()} isLoading={isLoading}>Apply Filters</Button>
              <Button variant="outline" onClick={handleClearFilters}>Clear</Button>
            </HStack>
          </Grid>
        </Box>

        {/* Error Message (if any) */}
        {error && (
          <Box p={4} mb={4} borderRadius="md" borderWidth="1px" borderColor="red.300" bg="red.50">
            <Text color="red.600">{error}</Text>
          </Box>
        )}
        
        {/* Loading Indicator */}
        {isLoading && (
          <Flex justify="center" align="center" height="200px">
            <Spinner size="xl" color="blue.500" />
          </Flex>
        )}

        {/* Analysis Results Section */}
        {!isLoading && data && (
          <Box>
            <Flex justifyContent="space-between" alignItems="center" mb={4}>
              <Heading size="md">Medical Necessity Analysis Results ({data.total_count} results)</Heading>
              <Button 
                colorScheme="blue" 
                size="sm" 
                onClick={loadAnalysisData} 
                isLoading={isLoading}
              >
                Refresh Data
              </Button>
            </Flex>
            
            {/* Analysis Summary */}
            <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4} mb={6}>
              <Box p={4} borderRadius="md" borderWidth="1px" borderColor="blue.200" bg="blue.50">
                <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                  {data.summary_stats.total_analyzed}
                </Text>
                <Text fontSize="sm" color="blue.600">Total Analyses</Text>
              </Box>
              <Box p={4} borderRadius="md" borderWidth="1px" borderColor="red.200" bg={errorBg}>
                <Text fontSize="2xl" fontWeight="bold" color="red.600">
                  {data.summary_stats.low_scores_count}
                </Text>
                <Text fontSize="sm" color="red.600">Low Scores (&lt;50)</Text>
              </Box>
              <Box p={4} borderRadius="md" borderWidth="1px" borderColor="green.200" bg={successBg}>
                <Text fontSize="2xl" fontWeight="bold" color="green.600">
                  {data.summary_stats.high_scores_count}
                </Text>
                <Text fontSize="sm" color="green.600">High Scores (≥85)</Text>
              </Box>
              <Box p={4} borderRadius="md" borderWidth="1px" borderColor={`${getScoreAttributes(data.summary_stats.avg_approval_score).colorScheme}.200`} bg={getScoreAttributes(data.summary_stats.avg_approval_score).bg}>
                <Text fontSize="2xl" fontWeight="bold" color={`${getScoreAttributes(data.summary_stats.avg_approval_score).colorScheme}.600`}>
                  {data.summary_stats.avg_approval_score}
                </Text>
                <Text fontSize="sm" color={`${getScoreAttributes(data.summary_stats.avg_approval_score).colorScheme}.600`}>Avg Score</Text>
              </Box>
            </Grid>

            {/* Analysis Results Table */}
            {data.results.length > 0 ? (
              <Box overflowX="auto" borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th>Patient/Case ID</Th>
                      <Th>Case Description</Th>
                      <Th>MN PDF</Th>
                      <Th>As Is Score</Th>
                      <Th>Best Case Score</Th>
                      <Th>Claim Type</Th>
                      <Th>CPT Codes</Th>
                      <Th>Payer</Th>
                      <Th>Analysis Date</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.results.map((result, index) => (
                      <Tr key={result.id}>
                        <Td>
                          <Link
                            href={`/analyze-case-mn/${result.external_case_id}?claim_type=${result.claim_type}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            fontWeight="medium"
                            color="blue.600"
                          >
                            {result.external_patient_id || result.external_case_id}
                          </Link>
                        </Td>
                        <Td title={result.case_description}>
                          {result.case_description?.substring(0, 50) + (result.case_description?.length > 50 ? '...' : '')}
                        </Td>
                        <Td>
                          {result.medical_necessity_pdf_id && (
                            <IconButton
                              icon={<ViewIcon />}
                              size="xs"
                              variant="outline"
                              colorScheme="blue"
                              aria-label="View PDF"
                              onClick={() => handleViewPdf(result.medical_necessity_pdf_id, result.external_case_id)}
                            />
                          )}
                        </Td>
                        <Td>
                          <Badge {...getApprovalScoreBadge(result.approval_assessment_score)}>
                            {result.approval_assessment_score}
                          </Badge>
                        </Td>
                        <Td>
                          {result.best_case_assessment_score > 0 ? (
                            <Badge {...getApprovalScoreBadge(result.best_case_assessment_score)}>
                              {result.best_case_assessment_score}
                            </Badge>
                          ) : (
                            <Text fontSize="xs" color="gray.400">-</Text>
                          )}
                        </Td>
                        <Td>
                          <Badge colorScheme="gray" size="sm">
                            {result.claim_type.toUpperCase()}
                          </Badge>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            {result.cpt_codes.slice(0, 2).map((cpt, idx) => (
                              <Text key={idx} fontSize="sm">
                                {cpt.code} ({cpt.units} unit{cpt.units !== 1 ? 's' : ''})
                              </Text>
                            ))}
                            {result.cpt_codes.length > 2 && (
                              <Text fontSize="xs" color="gray.500">
                                +{result.cpt_codes.length - 2} more
                              </Text>
                            )}
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium" fontSize="sm">
                              {result.payer_type || 'N/A'}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <Text fontSize="sm">
                            {formatDate(result.created_at)}
                          </Text>
                        </Td>
                        <Td>
                          <Button
                            size="xs"
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => window.open(`/analyze-case-mn/${result.external_case_id}?claim_type=${result.claim_type}`, '_blank')}
                          >
                            View Analysis
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <Box p={4} textAlign="center" borderRadius="md" borderWidth="1px">
                <Text>No analysis results found</Text>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Analysis Detail Modal */}
      {renderAnalysisModal()}
    </Box>
  );
}

export default AnalysisResults;
